<script>
    import dayjs from "dayjs";
    import advancedFormat from "dayjs/plugin/advancedFormat";
    dayjs.extend(advancedFormat);

    export let product;
    /** Stripe invoice once create_payment_intent has run — authoritative when present */
    export let invoice = null;
    /** Stripe's preview of the renewal invoice: { total, discount, currency, date } */
    export let nextInvoice = null;
    export let currency = '$';
    export let hasHardware = false;
    export let devicePrice = 0;
    export let discountedDevicePrice = 0;
    /** 0-5, once the fit step resolves a size */
    export let beltSize = null;
    /** { label, amount, currency } for the selected plan/interval */
    export let subscription = null;
    /** { days, autoRenew, renewalPrice, renewalCurrency, interval } */
    export let trial = null;
    export let isTrialActive = false;
    export let isFreeTrial = false;
    /** { code, savings, description } once a coupon validates */
    export let coupon = null;
    /**
     * Discounts the server applied without the shopper entering anything
     * (OTO staff perks, a coupon sitting on the Stripe customer):
     * [{ code, amount, currency }], amounts in cents.
     */
    export let autoAppliedCoupons = [];
    export let couponConfiguration = {};
    /** Resolved Stripe amounts for the config's fee price ids: { admin_fee, shipping } */
    export let feePrices = {};
    export let isResearch = false;
    export let study = null;
    export let location = null;
    export let organization = null;
    /** e.g. "$199.00/month" after a sponsored study ends */
    export let renewalPriceLabel = null;

    const currencies = { usd: '$', cad: 'CA$', eur: '€', gbp: '£', aud: 'AUD ' };

    // Imagery is managed on the Stripe product, so that is the only source.
    $: productImage = product?.stripe_product?.images?.[0] || null;
    $: freeShipping = !!couponConfiguration?.free_shipping;
    // Checkout bills shipping whenever free_shipping is off, falling back to the
    // default price when no override is configured. Gating this on shipping_price
    // hid the line on every org without an override and under-quoted the total.
    $: chargesShipping = !freeShipping;
    $: adminFee = !!couponConfiguration?.admin_fee_enabled;
    // The config only holds price ids; the amounts are resolved server-side.
    $: adminFeeAmount = adminFee && feePrices?.admin_fee ? feePrices.admin_fee.amount / 100 : 0;
    $: adminFeeDiscounted = coupon?.type === 'admin_fee' && adminFeeAmount
        ? (coupon.percent_off
            ? adminFeeAmount * (1 - coupon.percent_off / 100)
            : coupon.amount_off
                ? Math.max(0, adminFeeAmount - coupon.amount_off / 100)
                : adminFeeAmount)
        : adminFeeAmount;
    $: adminFeeLabel = feePrices?.admin_fee
        ? money(adminFeeDiscounted, feePrices.admin_fee.currency)
        : null;
    $: adminFeeOriginalLabel = coupon?.type === 'admin_fee' && adminFeeDiscounted < adminFeeAmount
        ? money(adminFeeAmount, feePrices.admin_fee.currency)
        : null;
    $: shippingLabel = feePrices?.shipping
        ? money(feePrices.shipping.amount / 100, feePrices.shipping.currency)
        : null;

    $: trialDuration = !trial?.days
        ? null
        : trial.days < 30
            ? `${trial.days}-day`
            : `${Math.round(trial.days / 30)}-month`;

    // A 365-day trial ends on the same calendar day next year. Without the year,
    // "October 19th" reads as today.
    function formatTrialEnd(date) {
        return date.year() === dayjs().year()
            ? date.format('MMMM Do')
            : date.format('MMMM D, YYYY');
    }

    $: trialPillLabel = [trialDuration, isFreeTrial ? 'free trial' : 'trial']
        .filter(Boolean)
        .join(' ');

    $: heading = isResearch ? 'Your study kit' : 'Your order';

    // Stripe knows whether the coupon survives into the renewal cycle (that depends on
    // the coupon duration), so its preview wins over the configured price whenever we have it.
    $: renewalDate = nextInvoice?.date
        ? formatTrialEnd(dayjs.unix(nextInvoice.date))
        : trial?.days
            ? formatTrialEnd(dayjs().add(Number(trial.days), 'day'))
            : null;

    $: renewalAmount = nextInvoice
        ? money(nextInvoice.total / 100, nextInvoice.currency)
        : trial?.renewalPrice != null
            ? money(trial.renewalPrice, null)
            : null;

    $: renewalDiscounted = !!nextInvoice?.discount;

    function money(amount, code) {
        const symbol = code ? (currencies[code] || '$') : currency;
        return `${symbol}${Number(amount || 0).toFixed(2)}`;
    }

    // Stripe returns invoice lines in its own order, so both the derived and the
    // invoice-backed lists get sorted into one fixed reading order.
    const RANKS = [
        { test: /sensor|kit/i, rank: 10 },
        { test: /electrode/i, rank: 20 },
        { test: /trial|subscription|plan|month|year/i, rank: 30 },
        { test: /admin fee/i, rank: 60 },
        { test: /shipping|delivery/i, rank: 50 },
    ];
    const BELT_RANK = 35;

    function rankOf(line) {
        if (line.rank != null) return line.rank;
        const match = RANKS.find(r => r.test.test(line.label || ''));
        return match ? match.rank : 40;
    }

    // What the sensor actually costs on this order, so the line item, the "included"
    // wording and the total can never disagree with each other.
    $: sensorPrice = hasHardware
        ? 0
        : ((discountedDevicePrice != null ? discountedDevicePrice : devicePrice) || 0);
    // A sensor the coupon paid for is not the same as one the kit includes, so a
    // hardware coupon shows the discount rather than borrowing the "Included" wording.
    $: hardwareCouponApplied = !hasHardware
        && coupon?.type === 'hardware'
        && devicePrice > 0
        && sensorPrice < devicePrice;
    $: sensorIncluded = !hasHardware && !hardwareCouponApplied && (isResearch || !sensorPrice);
    $: sensorCharge = sensorIncluded ? 0 : sensorPrice;

    function derivedLines(_hasHardware, _devicePrice, _sensorPrice, _sensorIncluded, _subscription, _beltSize, _freeShipping, _chargesShipping, _adminFee, _isFreeTrial, _isResearch, _shippingLabel, _adminFeeLabel, _adminFeeOriginalLabel) {
        const lines = [];

        if (!_hasHardware) {
            const sensorFree = _sensorIncluded || _sensorPrice <= 0;
            lines.push({
                label: 'OTO Bio Sensor',
                value: _sensorIncluded
                    ? (_isResearch ? 'Covered' : 'Included')
                    : (_sensorPrice <= 0 ? 'Free' : money(_sensorPrice)),
                original: !_sensorIncluded && _sensorPrice < _devicePrice ? money(_devicePrice) : null,
                free: sensorFree,
            });
            lines.push({ label: 'Electrodes Pack 50', value: _isResearch ? 'Covered' : 'Included', free: true });
        }

        if (_subscription && !_isResearch) {
            lines.push({
                label: _subscription.label,
                value: _isFreeTrial ? 'Free' : money(_subscription.amount, _subscription.currency),
                original: !_isFreeTrial && _subscription.original != null && _subscription.original > _subscription.amount
                    ? money(_subscription.original, _subscription.currency)
                    : null,
                free: _isFreeTrial,
            });
        } else if (_isResearch) {
            lines.push({ label: `${product?.nme || 'Subscription'} subscription`, value: 'Covered', free: true });
        }

        if (!_hasHardware) {
            if (_freeShipping || _isResearch) {
                lines.push({ label: 'Shipping', value: _isResearch ? 'Covered' : 'Free', free: true });
            } else if (_chargesShipping) {
                lines.push({
                    label: 'Shipping',
                    value: _shippingLabel || 'Calculated at payment',
                    muted: !_shippingLabel,
                });
            }
        }

        if (_adminFee && !_isResearch) {
            lines.push({
                label: 'Admin fee',
                value: _adminFeeLabel || 'Calculated at payment',
                original: _adminFeeOriginalLabel,
                muted: !_adminFeeLabel,
            });
        }

        return lines;
    }

    function invoiceLines(invoiceData) {
        return (invoiceData?.lines?.data || [])
            .filter(line => !line.metadata?.hide_in_ui)
            .map(line => {
                const discount = (line.discount_amounts || []).reduce((sum, d) => sum + d.amount, 0);
                const final = line.amount - discount;
                const label = line.type === 'invoiceitem' && line.quantity > 1
                    ? `${line.quantity} x ${line.description}`
                    : line.description;
                // Kit contents read "Included" both before and after the invoice
                // lands; "Free" is reserved for something that had a price and
                // was waived, which is why the struck-through original matters.
                const included = line.metadata?.kit_included === 'true';
                return {
                    label,
                    value: included
                        ? (isResearch ? 'Covered' : 'Included')
                        : (final <= 0 ? 'Free' : money(final / 100, invoiceData.currency)),
                    original: discount > 0 ? money(line.amount / 100, invoiceData.currency) : null,
                    free: included || final <= 0,
                };
            });
    }

    // The chosen belt size is ours, not Stripe's, so it survives the swap to invoice lines.
    $: beltLine = (beltSize !== null && beltSize !== undefined && !hasHardware)
        ? [{ label: 'Belt size', value: String(beltSize), muted: true, rank: BELT_RANK }]
        : [];

    $: lines = [
        ...(invoice
            ? invoiceLines(invoice)
            : derivedLines(hasHardware, devicePrice, sensorPrice, sensorIncluded, subscription, beltSize, freeShipping, chargesShipping, adminFee, isFreeTrial, isResearch, shippingLabel, adminFeeLabel, adminFeeOriginalLabel)),
        ...beltLine,
    ].sort((a, b) => rankOf(a) - rankOf(b));

    // Only Stripe knows what the coupon is actually worth against this order, so the
    // row stays at "Applied" until the invoice lands rather than guessing a figure.
    $: invoiceDiscount = invoice
        ? (invoice.total_discount_amounts || []).reduce((sum, d) => sum + d.amount, 0) / 100
        : 0;
    $: couponSavingsLabel = invoiceDiscount > 0
        ? `−${money(invoiceDiscount, invoice.currency)}`
        : 'Applied';

    $: hasUnknownCharges = !invoice
        && ((chargesShipping && !shippingLabel) || (adminFee && !adminFeeLabel));

    // Fees are billed up front, so a free trial can still have something due today.
    $: knownFees = isResearch
        ? 0
        : (!hasHardware && chargesShipping && feePrices?.shipping ? feePrices.shipping.amount / 100 : 0)
            + (adminFee ? adminFeeDiscounted : 0);

    // A free trial waives the subscription only. Hardware and fees are still charged
    // now, so the sensor stays in the total whenever its line shows a price.
    $: derivedTotal = isResearch
        ? 0
        : sensorCharge + (isFreeTrial ? 0 : (subscription?.amount || 0)) + knownFees;

    $: dueToday = invoice
        ? money((invoice.total || 0) / 100, invoice.currency)
        : money(derivedTotal, subscription?.currency);

    $: nothingDueToday = invoice ? (invoice.total || 0) === 0 : derivedTotal === 0;

    $: studyAccessLabel = study?.end_mode === 'duration' && study?.duration_months
        ? (study.duration_months === 1 ? '1 month from enrollment' : `${study.duration_months} months from enrollment`)
        : study?.end_date
            ? dayjs(study.end_date).format('D MMMM YYYY')
            : location?.research_end_date
                ? dayjs(location.research_end_date).format('D MMMM YYYY')
                : null;

    $: sponsorName = organization?.nme || location?.title || null;
</script>

<div class="order-summary">
    <div class="summary-head" class:head-badge-only={isTrialActive && !isResearch}>
        {#if isResearch}
            <h3>{heading}</h3>
            <span class="trial-pill">Sponsored</span>
        {:else if isTrialActive}
            <span class="trial-pill">{trialPillLabel}</span>
        {:else}
            <h3>{heading}</h3>
        {/if}
    </div>

    <div class="summary-product">
        {#if productImage}
            <img src={productImage} alt={product?.nme || ''} />
        {/if}
        <div>
            <p class="product-name">{product?.nme || 'OTO'}</p>
            {#if !hasHardware}
                <p class="product-blurb">Bio Sensor, 50-pack of electrodes, and the OTO app</p>
            {:else}
                <p class="product-blurb">App access for the OTO sensor you already own</p>
            {/if}
        </div>
    </div>

    <ul class="summary-lines">
        {#each lines as line (line.label + String(line.value))}
            <li>
                <span class="line-label">{line.label}</span>
                <span class="line-value" class:free={line.free} class:muted={line.muted}>
                    {#if line.original}<s>{line.original}</s>{/if}
                    {line.value}
                </span>
            </li>
        {/each}
        {#if coupon}
            <li class="coupon-line">
                <span class="line-label">
                    <i class="fa-solid fa-tag" aria-hidden="true"></i>
                    Coupon {coupon.code}{#if coupon.description} &middot; {coupon.description}{/if}
                </span>
                <span class="line-value free">{couponSavingsLabel}</span>
            </li>
        {/if}
        {#each autoAppliedCoupons as auto (auto.code)}
            <li class="coupon-line">
                <span class="line-label">
                    <i class="fa-solid fa-tag" aria-hidden="true"></i>
                    Auto-applied coupon: {auto.code}
                </span>
                <span class="line-value free">&minus;{money(auto.amount / 100, auto.currency)}</span>
            </li>
        {/each}
    </ul>

    <div class="summary-total">
        <span>{isResearch ? 'Cost to you' : 'Due today'}</span>
        <strong>{dueToday}</strong>
    </div>
    {#if hasUnknownCharges}
        <p class="summary-note-small">Shipping and fees are confirmed at the payment step.</p>
    {/if}

    {#if isResearch}
        <div class="summary-note">
            No payment method is collected.
            {#if sponsorName}
                <strong>{sponsorName}</strong> covers your kit and subscription
            {:else}
                The study covers your kit and subscription
            {/if}
            {#if studyAccessLabel}
                {#if study?.end_mode === 'duration'}
                    for {studyAccessLabel}.
                {:else}
                    through {studyAccessLabel}.
                {/if}
            {:else}
                .
            {/if}
            When it ends you can optionally renew at {renewalPriceLabel || 'the regular price'} — nothing renews on its own.
        </div>
    {:else if trial?.days}
        <div class="summary-note">
            {#if nothingDueToday}
                Your card is <strong>not charged today</strong>.
            {:else}
                <strong>{dueToday}</strong> is charged today.
            {/if}
            {#if trial.autoRenew && renewalAmount}
                On <strong>{renewalDate}</strong> the subscription renews at
                <strong>{renewalAmount}/{trial.interval || 'month'}</strong>
                {#if renewalDiscounted}(coupon applied){/if} unless you cancel.
            {:else}
                Your <strong>{trialDuration}</strong> trial ends <strong>{renewalDate}</strong> and will not renew automatically.
            {/if}
        </div>
    {/if}

    <ul class="summary-bullets">
        {#if sensorIncluded || isResearch}
            <li><i class="fa-solid fa-check" aria-hidden="true"></i> OTO Bio Sensor included at no charge</li>
        {/if}
        {#if freeShipping || isResearch}
            <li><i class="fa-solid fa-check" aria-hidden="true"></i> {isResearch ? 'Free shipping, both ways' : 'Free shipping'}</li>
        {/if}
    </ul>
</div>

<style lang="scss">
    .order-summary {
        font-family: Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 14px;
        padding: 20px;
        text-align: left;
        color: #1f2937;
    }

    .summary-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;

        &.head-badge-only {
            justify-content: flex-start;
        }

        h3 {
            font-family: inherit;
            font-size: 17px;
            font-weight: 700;
            margin: 0;
        }
    }

    .trial-pill {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #12a150;
        border: 1px solid #12a150;
        border-radius: 999px;
        padding: 3px 9px;
        white-space: nowrap;
    }

    .summary-product {
        display: flex;
        gap: 12px;
        align-items: flex-start;
        padding: 16px 0;
        border-bottom: 1px solid #eef0f3;

        img {
            width: 64px;
            height: 64px;
            border-radius: 10px;
            object-fit: cover;
            background: #f3f4f6;
            flex-shrink: 0;
        }
    }

    .product-name {
        font-weight: 600;
        font-size: 14px;
        margin: 0;
    }

    .product-blurb {
        font-size: 12px;
        color: #6b7280;
        margin: 2px 0 0;
        line-height: 1.4;
    }

    .summary-lines {
        list-style: none;
        margin: 0;
        padding: 14px 0;
        border-bottom: 1px solid #eef0f3;

        li {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 12px;
            font-size: 13px;
            padding: 5px 0;
        }
    }

    .line-label {
        color: #4b5563;
    }

    .coupon-line {
        .line-label {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            color: #12a150;
            font-weight: 500;
        }

        i {
            font-size: 11px;
        }
    }

    .line-value {
        font-weight: 600;
        white-space: nowrap;

        &.free {
            color: #12a150;
        }

        &.muted {
            color: #6b7280;
            font-weight: 500;
        }

        s {
            color: #9ca3af;
            font-weight: 400;
            margin-right: 6px;
        }
    }

    .summary-total {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        padding: 16px 0 4px;

        span {
            font-size: 15px;
            font-weight: 600;
        }

        strong {
            font-family: inherit;
            font-size: 26px;
            font-weight: 700;
        }
    }

    .summary-note-small {
        font-size: 11px;
        color: #9ca3af;
        margin: 0 0 8px;
    }

    .summary-note {
        background: #f8f9fb;
        border: none;
        border-radius: 10px;
        padding: 12px 14px;
        font-size: 13px;
        line-height: 1.5;
        color: #4b5563;
        margin-top: 8px;

        strong {
            font-family: inherit;
            font-weight: 700;
            color: #1f2937;
        }
    }

    .summary-bullets {
        list-style: none;
        margin: 14px 0 0;
        padding: 0;

        li {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            color: #4b5563;
            padding: 3px 0;
        }

        i {
            color: #12a150;
        }
    }
</style>
